import { Injectable, inject, signal, NgZone } from '@angular/core';
import { Firestore, collection, query, where, getDocs, doc, getDoc, updateDoc, orderBy, onSnapshot } from '@angular/fire/firestore';
import { Order, OrderStatus } from '../models/cake.model';
import { httpsCallable, Functions } from '@angular/fire/functions';
import { getISTDateString } from '../utils/date.utils';
import { VoiceAnnouncementService } from './voice-announcement.service';

export interface CallRiderResult {
  success: boolean;
  orderId: string;
  message: string;
  error?: 'RECHARGE_REQUIRED' | 'SHIPROCKET_ERROR' | string;
  details?: any;
  rider?: {
    name: string;
    eta: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AdminOrderService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private ngZone = inject(NgZone);
  private voiceService = inject(VoiceAnnouncementService);

  // Reactive signals for orders
  preparingOrders = signal<Order[]>([]);
  readyForPickupOrders = signal<Order[]>([]);
  outForDeliveryOrders = signal<Order[]>([]);
  completedOrders = signal<Order[]>([]);
  cancelledOrders = signal<Order[]>([]);
  pendingReviewOrders = signal<Order[]>([]);

  // New order detection signals
  readonly newPreparingOrders = signal<Order[]>([]);
  readonly newPendingReviewOrders = signal<Order[]>([]);

  // Track previous order IDs for diffing
  private previousPreparingIds = new Set<string>();
  private previousPendingReviewIds = new Set<string>();
  private isInitialLoad = { preparing: true, pendingReview: true };

  isLoading = signal(false);
  error = signal<string | null>(null);
  private _isListening = false;

  /**
   * Start listening for real-time order updates (safe to call multiple times)
   */
  startListening(): void {
    if (this._isListening) return;
    this._isListening = true;

    const ordersRef = collection(this.firestore, 'orders');

    // Subscribe to preparing orders
    const preparingQuery = query(ordersRef, where('status', '==', 'preparing'), orderBy('createdAt', 'desc'));
    onSnapshot(preparingQuery, (snapshot) => {
      this.ngZone.run(() => {
        const orders: Order[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));

        // Detect new orders (skip initial load)
        if (!this.isInitialLoad.preparing) {
          const currentIds = new Set(orders.map(o => o.id));
          const newOrders = orders.filter(o => !this.previousPreparingIds.has(o.id));

          if (newOrders.length > 0) {
            this.newPreparingOrders.set(newOrders);
            // Play voice announcement for each new order
            newOrders.forEach(order => {
              this.voiceService.playNewOrderAnnouncement(
                order.id,
                order.customerName,
                order.totalAmount,
                order.deliveryDate
              );
            });
          }
        }

        this.previousPreparingIds = new Set(orders.map(o => o.id));
        this.isInitialLoad.preparing = false;
        this.preparingOrders.set(orders);
      });
    });

    // Subscribe to ready_for_pickup orders
    const readyQuery = query(ordersRef, where('status', '==', 'ready_for_pickup'), orderBy('createdAt', 'desc'));
    onSnapshot(readyQuery, (snapshot) => {
      this.ngZone.run(() => {
        const orders: Order[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));
        this.readyForPickupOrders.set(orders);
      });
    });

    // Subscribe to out_for_delivery orders
    const deliveryQuery = query(ordersRef, where('status', '==', 'out_for_delivery'), orderBy('createdAt', 'desc'));
    onSnapshot(deliveryQuery, (snapshot) => {
      this.ngZone.run(() => {
        const orders: Order[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));
        this.outForDeliveryOrders.set(orders);
      });
    });

    // Subscribe to delivered orders
    const completedQuery = query(ordersRef, where('status', '==', 'delivered'), orderBy('createdAt', 'desc'));
    onSnapshot(completedQuery, (snapshot) => {
      this.ngZone.run(() => {
        const orders: Order[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));
        this.completedOrders.set(orders);
      });
    });

    // Subscribe to cancelled orders
    const cancelledQuery = query(ordersRef, where('status', '==', 'cancelled'), orderBy('createdAt', 'desc'));
    onSnapshot(cancelledQuery, (snapshot) => {
      this.ngZone.run(() => {
        const orders: Order[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));
        this.cancelledOrders.set(orders);
      });
    });

    // Subscribe to pending_review orders
    const pendingReviewQuery = query(ordersRef, where('status', '==', 'pending_review'), orderBy('createdAt', 'desc'));
    onSnapshot(pendingReviewQuery, (snapshot) => {
      this.ngZone.run(() => {
        const orders: Order[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));

        // Detect new pending review orders (skip initial load)
        if (!this.isInitialLoad.pendingReview) {
          const currentIds = new Set(orders.map(o => o.id));
          const newOrders = orders.filter(o => !this.previousPendingReviewIds.has(o.id));

          if (newOrders.length > 0) {
            this.newPendingReviewOrders.set(newOrders);
            // Play voice announcement for each new pending review order
            newOrders.forEach(order => {
              this.voiceService.playPendingReviewAnnouncement(order.id);
            });
          }
        }

        this.previousPendingReviewIds = new Set(orders.map(o => o.id));
        this.isInitialLoad.pendingReview = false;
        this.pendingReviewOrders.set(orders);
      });
    });
  }

  /**
   * Fetch a single order by ID
   */
  async getOrderById(orderId: string): Promise<Order | null> {
    try {
      const orderDoc = doc(this.firestore, `orders/${orderId}`);
      const docSnap = await getDoc(orderDoc);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Order;
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching order:', error);
      this.error.set('Failed to load order');
      return null;
    }
  }

  /**
   * Get all pickup locations from Shiprocket
   */
  async getPickupLocations(): Promise<any> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const getPickupLocationsFn = httpsCallable<{}, any>(
        this.functions,
        'getPickupLocations'
      );

      const result = await getPickupLocationsFn({});
      console.log('Get Pickup Locations result:', result.data);
      
      this.isLoading.set(false);
      return result.data || null;
    } catch (error: any) {
      console.error('Get Pickup Locations error:', error);
      this.error.set(error.message || 'Failed to get pickup locations');
      this.isLoading.set(false);
      return null;
    }
  }

  /**
   * Geocode Address - Convert address to coordinates
   * Uses Google Maps Geocoding API or returns demo coordinates
   */
  async geocodeAddress(address: string): Promise<any> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const geocodeAddressFn = httpsCallable<{ address: string }, any>(
        this.functions,
        'geocodeAddress'
      );

      const result = await geocodeAddressFn({ address });
      console.log('Geocode Address result:', result.data);
      
      this.isLoading.set(false);
      return result.data || null;
    } catch (error: any) {
      console.error('Geocode Address error:', error);
      this.error.set(error.message || 'Failed to geocode address');
      this.isLoading.set(false);
      return null;
    }
  }

  /**
   * Check Courier Serviceability
   * Validates if a delivery location is serviceable by couriers
   */
  async checkCourierServiceability(params: {
    pickup_postcode: number;
    delivery_postcode: number;
    cod: boolean;
    is_new_hyperlocal: boolean;
    lat_from: number;
    long_from: number;
    lat_to: number;
    long_to: number;
  }): Promise<any> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const checkServiceabilityFn = httpsCallable<any, any>(
        this.functions,
        'checkCourierServiceability'
      );

      const result = await checkServiceabilityFn(params);
      console.log('Check Courier Serviceability result:', result.data);
      
      this.isLoading.set(false);
      return result.data || null;
    } catch (error: any) {
      console.error('Check Courier Serviceability error:', error);
      this.error.set(error.message || 'Failed to check courier serviceability');
      this.isLoading.set(false);
      return null;
    }
  }

  /**
   * Manually update order status
   */
  async updateOrderStatus(orderId: string, status: OrderStatus): Promise<boolean> {
    try {
      const orderDoc = doc(this.firestore, `orders/${orderId}`);
      await updateDoc(orderDoc, { 
        status,
        status_updated_at: new Date().toISOString()
      });
      console.log('Order status updated:', orderId, status);
      return true;
    } catch (error: any) {
      console.error('Error updating order status:', error);
      this.error.set('Failed to update order status');
      return false;
    }
  }

  /**
   * Update arbitrary order fields in Firestore
   */
  async updateOrder(orderId: string, data: Record<string, any>): Promise<boolean> {
    try {
      const orderDoc = doc(this.firestore, `orders/${orderId}`);
      await updateDoc(orderDoc, {
        ...data,
        updated_at: new Date().toISOString()
      });
      console.log('Order updated:', orderId, data);
      return true;
    } catch (error: any) {
      console.error('Error updating order:', error);
      this.error.set('Failed to update order');
      return false;
    }
  }

  /**
   * Sync Order Status from Shiprocket
   * Fetches real-time tracking and updates Firestore
   */
  async syncOrderStatus(orderId: string): Promise<any> {
    this.error.set(null);

    try {
      const syncStatusFn = httpsCallable<{ orderId: string }, any>(
        this.functions,
        'syncOrderStatus'
      );

      const result = await syncStatusFn({ orderId });
      console.log('Sync Order Status result:', result.data);
      
      return result.data || null;
    } catch (error: any) {
      console.error('Sync Order Status error:', error);
      this.error.set(error.message || 'Failed to sync order status');
      return null;
    }
  }

  /**
   * Get Tracking Status from Shiprocket
   * Fetches tracking details using AWB code
   */
  async getTrackingStatus(awbCode: string): Promise<any> {
    this.error.set(null);

    try {
      const getTrackingFn = httpsCallable<{ awbCode: string }, any>(
        this.functions,
        'getTrackingStatus'
      );

      const result = await getTrackingFn({ awbCode });
      console.log('Get Tracking Status result:', result.data);
      
      return result.data || null;
    } catch (error: any) {
      console.error('Get Tracking Status error:', error);
      this.error.set(error.message || 'Failed to get tracking status');
      return null;
    }
  }


  /**
   * Get all orders for today
   */
  async getTodayOrders(): Promise<Order[]> {
    this.isLoading.set(true);

    try {
      const today = getISTDateString();
      const ordersRef = collection(this.firestore, 'orders');
      const q = query(ordersRef, where('deliveryDate', '==', today));
      
      const snapshot = await getDocs(q);
      const orders: Order[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Order));
      
      this.isLoading.set(false);
      return orders;
    } catch (error: any) {
      console.error('Error fetching today orders:', error);
      this.error.set('Failed to load orders');
      this.isLoading.set(false);
      return [];
    }
  }

  /**
   * Get orders scheduled for a specific date
   */
  async getOrdersByDate(date: string): Promise<Order[]> {
    try {
      const ordersRef = collection(this.firestore, 'orders');
      const q = query(ordersRef, where('deliveryDate', '==', date));
      
      const snapshot = await getDocs(q);
      const orders: Order[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Order));
      
      return orders;
    } catch (error: any) {
      console.error('Error fetching orders by date:', error);
      return [];
    }
  }

  /**
   * Manually refresh all order signals by fetching latest data
   */
  async refresh(): Promise<void> {
    this.isLoading.set(true);
    try {
      const ordersRef = collection(this.firestore, 'orders');

      const [preparingSnap, readySnap, deliverySnap, completedSnap, cancelledSnap, pendingReviewSnap] = await Promise.all([
        getDocs(query(ordersRef, where('status', '==', 'preparing'), orderBy('createdAt', 'desc'))),
        getDocs(query(ordersRef, where('status', '==', 'ready_for_pickup'), orderBy('createdAt', 'desc'))),
        getDocs(query(ordersRef, where('status', '==', 'out_for_delivery'), orderBy('createdAt', 'desc'))),
        getDocs(query(ordersRef, where('status', '==', 'delivered'), orderBy('createdAt', 'desc'))),
        getDocs(query(ordersRef, where('status', '==', 'cancelled'), orderBy('createdAt', 'desc'))),
        getDocs(query(ordersRef, where('status', '==', 'pending_review'), orderBy('createdAt', 'desc')))
      ]);

      this.preparingOrders.set(preparingSnap.docs.map(d => ({ id: d.id, ...d.data() } as Order)));
      this.readyForPickupOrders.set(readySnap.docs.map(d => ({ id: d.id, ...d.data() } as Order)));
      this.outForDeliveryOrders.set(deliverySnap.docs.map(d => ({ id: d.id, ...d.data() } as Order)));
      this.completedOrders.set(completedSnap.docs.map(d => ({ id: d.id, ...d.data() } as Order)));
      this.cancelledOrders.set(cancelledSnap.docs.map(d => ({ id: d.id, ...d.data() } as Order)));
      this.pendingReviewOrders.set(pendingReviewSnap.docs.map(d => ({ id: d.id, ...d.data() } as Order)));
    } catch (error: any) {
      console.error('Error refreshing orders:', error);
      this.error.set('Failed to refresh orders');
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Get pending orders count
   */
  getPreparingCount(): number {
    return this.preparingOrders().length;
  }

  /**
   * Get orders ready for pickup count
   */
  getReadyCount(): number {
    return this.readyForPickupOrders().length;
  }

  /**
   * Get orders out for delivery count
   */
  getDeliveryCount(): number {
    return this.outForDeliveryOrders().length;
  }

  /**
   * Register Pickup Location via Firebase Cloud Function
   * Calls Shiprocket API to register a new pickup location
   */
  async registerPickupLocation(pickupData: any): Promise<any> {
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const registerPickupFn = httpsCallable<any, any>(
        this.functions,
        'addPickupLocation'
      );

      const result = await registerPickupFn(pickupData);
      console.log('Register Pickup Location result:', result.data);
      
      this.isLoading.set(false);
      return result.data || null;
    } catch (error: any) {
      console.error('Register Pickup Location error:', error);
      this.error.set(error.message || 'Failed to register pickup location');
      this.isLoading.set(false);
      return null;
    }
  }

  /**
   * Call Rider for Order Pickup
   * Triggers Shiprocket to assign a rider for order pickup
   */
  async callRider(orderId: string): Promise<CallRiderResult | null> {
    this.error.set(null);

    try {
      const callRiderFn = httpsCallable<{ orderId: string }, CallRiderResult>(
        this.functions,
        'callRider'
      );

      const result = await callRiderFn({ orderId });
      console.log('Call Rider result:', result.data);
      
      return result.data || null;
    } catch (error: any) {
      console.error('Call Rider error:', error);
      this.error.set(error.message || 'Failed to call rider');
      return null;
    }
  }

  /**
   * Cancel an order - Updates Firestore status, restores inventory/capacity,
   * initiates Razorpay refund, and sends customer email notification.
   */
  async cancelOrder(orderId: string, reason?: string): Promise<{ success: boolean; message?: string; refundId?: string }> {
    // Note: isLoading is NOT set here because the component handles
    // its own button-level loading state (cancellingOrderId signal).
    // Global skeleton is unnecessary and distracting for panel actions.
    this.error.set(null);

    try {
      const cancelOrderFn = httpsCallable<{ orderId: string; reason?: string }, any>(
        this.functions,
        'cancelOrderStatus'
      );

      const result = await cancelOrderFn({ orderId, reason });
      console.log('Cancel Order result:', result.data);
      
      return {
        success: result.data?.success || false,
        message: result.data?.message,
        refundId: result.data?.refundId
      };
    } catch (error: any) {
      console.error('Cancel Order error:', error);
      this.error.set(error.message || 'Failed to cancel order');
      return { success: false, message: error.message || 'Failed to cancel order' };
    }
  }

  /**
   * Bulk cancel orders - delivery partner agnostic
   * Calls cancelOrderStatus Cloud Function for each order individually
   */
  async bulkCancelOrders(orderIds: string[]): Promise<{ success: boolean; cancelled: number; failed: number; errors: string[] }> {
    this.isLoading.set(true);
    this.error.set(null);

    const cancelOrderStatusFn = httpsCallable<{ orderId: string; reason?: string }, any>(
      this.functions,
      'cancelOrderStatus'
    );

    let cancelled = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const orderId of orderIds) {
      try {
        const result = await cancelOrderStatusFn({ orderId, reason: 'Bulk cancel by admin' });
        if (result.data?.success) {
          cancelled++;
        } else {
          failed++;
          errors.push(`Order ${orderId}: ${result.data?.message || 'Unknown error'}`);
        }
      } catch (error: any) {
        failed++;
        const msg = error?.message || `Failed to cancel order ${orderId}`;
        errors.push(`Order ${orderId}: ${msg}`);
        console.error(`Cancel order ${orderId} error:`, error);
      }
    }

    this.isLoading.set(false);

    if (failed > 0 && cancelled === 0) {
      this.error.set(errors.join('; '));
      return { success: false, cancelled, failed, errors };
    }

    return { success: true, cancelled, failed, errors };
  }

  /**
   * Approve a pending_review order and commit stock + capacity atomically.
   */
  async approvePendingReview(orderId: string): Promise<{ success: boolean; message?: string }> {
    // Note: isLoading is NOT set here because the component handles
    // its own button-level loading state (approvingOrderId signal).
    // Global skeleton is unnecessary and distracting for panel actions.
    this.error.set(null);

    try {
      const approveFn = httpsCallable<{ orderId: string }, { success: boolean; message?: string }>(
        this.functions,
        'approvePendingReview'
      );
      const result = await approveFn({ orderId });
      return result.data;
    } catch (error: any) {
      const msg = error?.message || 'Failed to approve order';
      this.error.set(msg);
      console.error('Approve pending review error:', error);
      throw error;
    }
  }

  /**
   * Reject a pending_review order and initiate a Razorpay refund.
   */
  async rejectPendingReview(orderId: string, reason?: string): Promise<{ success: boolean; message?: string; refundId?: string }> {
    this.error.set(null);

    try {
      const rejectFn = httpsCallable<{ orderId: string; reason?: string }, { success: boolean; message?: string; refundId?: string }>(
        this.functions,
        'rejectPendingReview'
      );
      const result = await rejectFn({ orderId, reason });
      return result.data;
    } catch (error: any) {
      const msg = error?.message || 'Failed to reject and refund order';
      this.error.set(msg);
      console.error('Reject pending review error:', error);
      throw error;
    }
  }
}
